import { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, AlertCircle, CheckCircle, RefreshCw, WifiOff } from 'lucide-react';
import Button from '../../ui/Button';
import { Card, CardContent } from '../../ui/Card';
import irrigationNetworkService, {
  type LiveAnalysis,
  type SensorDivergence,
  type DivergenceLevel,
} from '../../../services/irrigationNetwork';
import { errorMessage } from '../../../utils/apiResponse';
import { formatTimeInTz, useOrgTimezone } from '../../../utils/timezone';

interface Props {
  farmId: number;
  data: LiveAnalysis | null;
  setData: (d: LiveAnalysis | null) => void;
  onJump: (target: { node_id?: number; pipe_id?: number }) => void;
}

const POLL_MS = 60_000;

export default function LiveAnalysisPanel({ farmId, data, setData, onJump }: Props) {
  const orgTz = useOrgTimezone();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const r = await irrigationNetworkService.getLiveAnalysis(farmId);
      setData(r);
    } catch (e) { setError(errorMessage(e, 'Live analysis failed')); }
    finally { setLoading(false); }
  }, [farmId, setData]);

  useEffect(() => {
    refresh();
    const t = setInterval(() => refresh(true), POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  const sumByLevel = (lvl: DivergenceLevel) =>
    data?.divergences.filter(d => d.level === lvl).length ?? 0;

  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-sm flex items-center gap-1">
            <Activity className="w-4 h-4 text-purple-600" /> Live divergence
          </h3>
          <button onClick={() => refresh()} disabled={loading}
            className="text-xs flex items-center gap-1 text-blue-600 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {error && <div className="text-red-600 text-xs mb-2">{error}</div>}

        {data && (
          <>
            <div className="text-xs text-gray-600 mb-2">
              {data.summary.open_valves} valves on · {data.summary.total_demand_lpm} L/min total
              {data.summary.routers_offline > 0 && (
                <span className="ml-2 text-amber-700 inline-flex items-center gap-1">
                  <WifiOff className="w-3 h-3" /> {data.summary.routers_offline} router(s) offline
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-1 text-xs mb-3">
              <Pill color="green" label="OK"     value={sumByLevel('ok')} />
              <Pill color="amber" label="Warn"   value={sumByLevel('warn')} />
              <Pill color="red"   label="Alert"  value={sumByLevel('alert')} />
              <Pill color="gray"  label="Stale"  value={sumByLevel('stale')} />
            </div>

            {data.divergences.length === 0 ? (
              <div className="text-xs text-gray-500">
                No pressure sensors linked. Add a pressure-type sensor and link it to a valve or junction node to enable divergence detection.
              </div>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {data.divergences
                  .slice()
                  .sort((a, b) => severityRank(b.level) - severityRank(a.level))
                  .map((d) => <DivergenceRow key={d.node_id} d={d} onClick={() => onJump({ node_id: d.node_id })} />)}
              </div>
            )}

            {data.solve.warnings.length > 0 && (
              <div className="mt-3 border-t pt-2">
                <div className="text-xs font-semibold mb-1">Solver warnings ({data.solve.warnings.length})</div>
                <div className="space-y-0.5 text-xs max-h-32 overflow-y-auto">
                  {data.solve.warnings.map((w, i) => (
                    <button key={i} className="text-left block w-full hover:underline"
                      onClick={() => onJump({ node_id: w.node_id, pipe_id: w.pipe_id })}>
                      <span className={
                        w.level === 'error' ? 'text-red-700' :
                        w.level === 'warn'  ? 'text-amber-700' : 'text-blue-700'
                      }>• {w.message}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-2 text-[10px] text-gray-400">
              Solved at {formatTimeInTz(data.generated_at, orgTz)}. Auto-refresh every 60 s.
            </div>

            <div className="mt-2">
              <Button size="sm" variant="outline" className="w-full" onClick={() => setData(null)}>
                Hide live overlay
              </Button>
            </div>
          </>
        )}

        {!data && loading && <div className="text-xs text-gray-500">Solving current state…</div>}
      </CardContent>
    </Card>
  );
}

function severityRank(level: DivergenceLevel): number {
  return level === 'alert' ? 4 : level === 'warn' ? 3 : level === 'stale' ? 2 : level === 'unknown' ? 1 : 0;
}

function Pill({ color, label, value }: { color: 'green' | 'amber' | 'red' | 'gray'; label: string; value: number }) {
  const cls =
    color === 'green' ? 'bg-green-100 text-green-700' :
    color === 'amber' ? 'bg-amber-100 text-amber-700' :
    color === 'red'   ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700';
  return <div className={`px-2 py-0.5 rounded ${cls}`}><span className="font-semibold">{value}</span> {label}</div>;
}

function DivergenceRow({ d, onClick }: { d: SensorDivergence; onClick: () => void }) {
  const icon =
    d.level === 'alert' ? <AlertCircle className="w-3 h-3 text-red-600" /> :
    d.level === 'warn'  ? <AlertTriangle className="w-3 h-3 text-amber-600" /> :
    d.level === 'stale' ? <WifiOff className="w-3 h-3 text-gray-500" /> :
                          <CheckCircle className="w-3 h-3 text-green-600" />;
  return (
    <button onClick={onClick} className="w-full text-left flex items-center gap-2 px-2 py-1 rounded text-xs hover:bg-gray-50">
      {icon}
      <span className="flex-1 truncate">{d.node_name}</span>
      <span className="text-gray-600">
        {d.measured_kpa.toFixed(0)}
        {d.predicted_kpa != null && <span className="text-gray-400"> / {d.predicted_kpa.toFixed(0)}</span>}
        <span className="text-gray-400"> kPa</span>
        {d.delta_pct != null && (
          <span className={`ml-1 ${Math.abs(d.delta_pct) >= 20 ? 'text-amber-700' : 'text-gray-500'}`}>
            ({d.delta_pct > 0 ? '+' : ''}{d.delta_pct.toFixed(0)}%)
          </span>
        )}
      </span>
    </button>
  );
}
